'use client'

import { useState, useMemo, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import CatalogHero from '@/components/marketplace/CatalogHero'
import CatalogSearch from '@/components/marketplace/CatalogSearch'
import CatalogTabs from '@/components/marketplace/CatalogTabs'
import MarketplaceSearchBar from '@/components/marketplace/MarketplaceSearchBar'
import { ProductCard } from '@/components/marketplace/ProductCard'
import { RequestProductCard } from '@/components/marketplace/RequestProductCard'
import { SearchEmptyState } from '@/components/marketplace/SearchEmptyState'
import RollingNumber from '@/components/marketplace/RollingNumber'
import DefenseCrossSell from '@/components/marketplace/DefenseCrossSell'
import {
  FLAGSHIP_PRODUCTS,
  GOOGLE_WORKSPACE_RECOMMENDED,
  RECOMMENDED_PRODUCTS,
  type RecommendedProduct,
} from '@/constants/marketplaceRecommended'
import { MICROSOFT_CATEGORIES } from '@/constants/microsoftCategories'
import type { CatalogItem, PricingSegment, MicrosoftCategorySlug } from '@/types/marketplace'
import { searchCatalog } from '@/utils/marketplaceSearch'

const GOOGLE_WORKSPACE_TAB = {
  slug: 'google-workspace',
  label: 'Google Workspace',
  href: '/marketplace/google-workspace',
}
const GOOGLE_WORKSPACE_HEADER = {
  name: 'Google Workspace',
  description:
    'Business, Enterprise, and Education plans, plus Gemini, Google Voice, Vault, AppSheet, and extra storage. Quote them alongside your Microsoft licenses.',
}

const CATEGORY_TABS = [
  { slug: 'recommended', label: 'Recommended' },
  ...MICROSOFT_CATEGORIES.map((c) => ({ slug: c.slug, label: c.shortName })),
].map((tab) => ({ ...tab, href: buildCatalogUrl(tab.slug, 'all') }))

const EMPTY_SEARCH_CATEGORIES = CATEGORY_TABS.filter((tab) =>
  ['security-identity', 'compliance-governance', 'microsoft-365-suites', 'infrastructure-management'].includes(
    tab.slug,
  ),
)

const SEGMENT_PILLS: { value: PricingSegment | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'education', label: 'Education' },
  { value: 'charity', label: 'Charity' },
  { value: 'gcc-high', label: 'GCC High' },
  { value: 'gcc', label: 'GCC' },
  { value: 'gcon', label: 'GCON' },
  { value: 'secret', label: 'Secret' },
  { value: 'top-secret', label: 'Top Secret' },
]

const FLAGSHIP_SLUGS = FLAGSHIP_PRODUCTS.map((pick) => pick.slug)

const INITIAL_SHOW = 9
const SEARCH_INITIAL_SHOW = 24

const RECOMMENDED_GROUPS = [
  { key: 'smb', title: 'Bestsellers for small & medium businesses' },
  { key: 'enterprise', title: 'Bestsellers for enterprise' },
  { key: 'standalone', title: 'Standalone solutions' },
  { key: 'googleWorkspace', title: 'Google Workspace' },
] as const

const SECTION_TITLE_CLASSES = 'text-[28px] font-medium leading-9 tracking-[-0.5px] text-primary-ink-100'
const SECTION_SUBTITLE_CLASSES = 'mt-1 text-base text-primary-ink-60'

const GRID_CLASSES = 'grid gap-6 sm:grid-cols-2 xl:grid-cols-3'

interface MicrosoftCatalogClientProps {
  products: CatalogItem[]
  initialCategory?: string
  initialSegment?: PricingSegment | 'all'
}

/** Build a path-based URL for the catalog */
function buildCatalogUrl(cat: string, seg: string): string {
  const base = '/marketplace/microsoft'
  if (cat === 'recommended' && seg === 'all') return '/marketplace'
  // Google Workspace has no segment routes; its pills still filter, they just don't change the URL
  if (cat === GOOGLE_WORKSPACE_TAB.slug) return GOOGLE_WORKSPACE_TAB.href
  if (cat === 'recommended' && seg !== 'all') return `${base}/segment/${seg}`
  if (seg === 'all') return `${base}/category/${cat}`
  return `${base}/category/${cat}/${seg}`
}

function LoadMoreButton({ remaining, onClick }: { remaining: number; onClick: () => void }) {
  return (
    <div className="mt-8 text-center">
      <button
        type="button"
        onClick={onClick}
        className="h-9 rounded-md border border-primary-ink-10 bg-white px-4 text-sm font-medium text-primary-ink-100 transition-colors hover:border-primary-ink-30">
        Load more ({remaining} more products)
      </button>
    </div>
  )
}

export default function MicrosoftCatalogClient({
  products,
  initialCategory = 'recommended',
  initialSegment = 'all',
}: MicrosoftCatalogClientProps) {
  const [activeCategory, setActiveCategory] = useState(initialCategory)
  const [activeSegment, setActiveSegment] = useState<PricingSegment | 'all'>(initialSegment)
  const [sortBy, setSortBy] = useState<'popularity' | 'price-asc' | 'price-desc'>('popularity')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [searchQuery, setSearchQuery] = useState('')
  // Holding the expanded query, not a boolean, collapses the results again whenever the query changes
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null)
  const searchGridRef = useRef<HTMLDivElement>(null)
  // Remounting the search on a tab click drops its typed text and any search still waiting on the debounce
  const [searchKey, setSearchKey] = useState(0)

  // Update URL when segment or category changes
  const updateUrl = useCallback((cat: string, seg: string) => {
    window.history.replaceState(null, '', buildCatalogUrl(cat, seg))
  }, [])

  const searchFiltered = useMemo(() => searchCatalog(products, searchQuery), [products, searchQuery])

  // Category + segment filter
  const categoryFiltered = useMemo(() => {
    let items = products.filter((p) => p.category === activeCategory)
    if (activeSegment !== 'all') {
      items = items.filter((p) => p.segments.some((s) => s.segment === activeSegment))
    }
    return items
  }, [products, activeCategory, activeSegment])

  // Sort
  const sorted = useMemo(() => {
    const items = [...categoryFiltered]
    if (sortBy === 'price-asc') return items.sort((a, b) => (a.lowestPrice ?? Infinity) - (b.lowestPrice ?? Infinity))
    if (sortBy === 'price-desc') return items.sort((a, b) => (b.lowestPrice ?? 0) - (a.lowestPrice ?? 0))
    // Google rows have no stable slug to list in FLAGSHIP_PRODUCTS, so their core plans rank by name after it
    const rank = (item: CatalogItem) => {
      const flagship = FLAGSHIP_SLUGS.indexOf(item.slug)
      if (flagship !== -1) return flagship
      const google = GOOGLE_WORKSPACE_RECOMMENDED.indexOf(item.displayName)
      return google === -1 ? -1 : FLAGSHIP_SLUGS.length + google
    }
    // Popularity: flagship order first, then price desc
    return items.sort((a, b) => {
      const ai = rank(a)
      const bi = rank(b)
      if (ai !== -1 && bi !== -1) return ai - bi
      if (ai !== -1) return -1
      if (bi !== -1) return 1
      return (b.lowestPrice ?? 0) - (a.lowestPrice ?? 0)
    })
  }, [categoryFiltered, sortBy])

  const handleCategoryChange = useCallback(
    (slug: string) => {
      setActiveCategory(slug)
      setSearchQuery('')
      setSearchKey((key) => key + 1)
      setActiveSegment('all')
      updateUrl(slug, 'all')
    },
    [updateUrl],
  )

  // The tab, segment, and sort are left alone while searching, so clearing the search returns to them
  const isSearchMode = searchQuery.trim().length > 0
  const searchExpanded = expandedQuery === searchQuery
  const isRecommended = activeCategory === 'recommended' && !isSearchMode

  // Category info for header
  const catConfig =
    activeCategory === GOOGLE_WORKSPACE_TAB.slug
      ? GOOGLE_WORKSPACE_HEADER
      : MICROSOFT_CATEGORIES.find((c) => c.slug === activeCategory)

  // The tab shows only once Rails sends Google Workspace rows, so an empty catalog never offers an empty tab
  const categoryTabs = useMemo(
    () =>
      products.some((p) => p.category === GOOGLE_WORKSPACE_TAB.slug)
        ? [...CATEGORY_TABS, GOOGLE_WORKSPACE_TAB]
        : CATEGORY_TABS,
    [products],
  )

  // Rails carries no Secret or Top Secret rows today, so a pill shows only when the category has products in it
  const segmentPills = useMemo(() => {
    const inCategory = new Set(
      products.filter((p) => p.category === activeCategory).flatMap((p) => p.segments.map((s) => s.segment)),
    )
    return SEGMENT_PILLS.filter(
      (pill) => pill.value === 'all' || pill.value === activeSegment || inCategory.has(pill.value),
    )
  }, [products, activeCategory, activeSegment])

  // Recommended sections
  const recommendedSections = useMemo(() => {
    if (!isRecommended) return null
    const bySlug = new Map(products.map((p) => [p.slug, p]))
    const resolve = (picks: RecommendedProduct[]) =>
      picks.flatMap((pick) => {
        const product = bySlug.get(pick.slug)
        return product ? [{ product, segment: pick.segment }] : []
      })
    return {
      smb: resolve(RECOMMENDED_PRODUCTS.smb),
      enterprise: resolve(RECOMMENDED_PRODUCTS.enterprise),
      standalone: resolve(RECOMMENDED_PRODUCTS.standalone),
      googleWorkspace: GOOGLE_WORKSPACE_RECOMMENDED.flatMap((name) => {
        const product = products.find((p) => p.displayName === name && p.segments[0]?.segment === 'commercial')
        return product ? [{ product, segment: undefined }] : []
      }),
    }
  }, [isRecommended, products])

  return (
    <section id="catalog" className="overflow-x-clip">
      <CatalogHero />

      {/* Matches the sticky navbar height, so Browse licenses scrolls the hero fully out of view */}
      <div id="browse" className="scroll-mt-[85px] bg-secondary-gray-10 lg:scroll-mt-[69px]">
        <div className="container pb-10 pt-2 lg:py-12">
          <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-10">
            <CatalogTabs
              tabs={categoryTabs}
              active={isSearchMode ? null : activeCategory}
              onChange={handleCategoryChange}
            />
            <div className="min-w-0">
              <MarketplaceSearchBar>
                <CatalogSearch key={searchKey} onChange={setSearchQuery} />
              </MarketplaceSearchBar>
              <div className="mt-5">
                {/* Search results mode */}
                {isSearchMode && (
                  <>
                    <p className="mb-6 text-sm text-primary-ink-60">
                      <RollingNumber value={String(searchFiltered.length)} />{' '}
                      {searchFiltered.length === 1 ? 'product' : 'products'} matching &ldquo;{searchQuery}&rdquo;
                    </p>
                    {searchFiltered.length === 0 ? (
                      <SearchEmptyState
                        query={searchQuery}
                        categories={EMPTY_SEARCH_CATEGORIES}
                        onClear={() => {
                          setSearchQuery('')
                          setSearchKey((key) => key + 1)
                        }}
                        onBrowse={handleCategoryChange}
                      />
                    ) : (
                      <div ref={searchGridRef} className={GRID_CLASSES}>
                        {searchFiltered
                          .slice(0, searchExpanded ? searchFiltered.length : SEARCH_INITIAL_SHOW)
                          .map((p) => (
                            <ProductCard key={p.slug} product={p} headingLevel={2} />
                          ))}
                        {(searchExpanded || searchFiltered.length <= SEARCH_INITIAL_SHOW) && (
                          <RequestProductCard query={searchQuery} />
                        )}
                      </div>
                    )}

                    {!searchExpanded && searchFiltered.length > SEARCH_INITIAL_SHOW && (
                      <LoadMoreButton
                        remaining={searchFiltered.length - SEARCH_INITIAL_SHOW}
                        onClick={() => {
                          // The button unmounts on click, so focus would otherwise drop to <body>
                          flushSync(() => setExpandedQuery(searchQuery))
                          searchGridRef.current
                            ?.querySelectorAll('article')
                            [SEARCH_INITIAL_SHOW]?.querySelector('a')
                            ?.focus()
                        }}
                      />
                    )}
                  </>
                )}

                {/* Recommended mode */}
                {isRecommended && recommendedSections && (
                  <>
                    <header className="mb-10">
                      <h2 className={SECTION_TITLE_CLASSES}>Recommended</h2>
                      <p className={SECTION_SUBTITLE_CLASSES}>
                        Popular Microsoft and Google Workspace licenses across enterprise, business, and standalone
                        plans.
                      </p>
                    </header>

                    {RECOMMENDED_GROUPS.filter((group) => recommendedSections[group.key].length > 0).map((group) => (
                      <section key={group.key} className="mb-12">
                        <h3 className="mb-4 text-lg font-medium leading-7 tracking-[-0.2px] text-primary-ink-100">
                          {group.title}
                        </h3>
                        <div className={GRID_CLASSES}>
                          {recommendedSections[group.key].map(({ product, segment }) => (
                            <ProductCard
                              key={`${product.slug}:${segment ?? 'all'}`}
                              product={product}
                              headingLevel={4}
                              activeSegment={segment ?? (activeSegment !== 'all' ? activeSegment : undefined)}
                            />
                          ))}
                        </div>
                      </section>
                    ))}
                  </>
                )}

                {/* Category mode */}
                {!isRecommended && !isSearchMode && (
                  <>
                    <header className="mb-6">
                      <h2 className={SECTION_TITLE_CLASSES}>{catConfig?.name ?? activeCategory}</h2>
                      {catConfig?.description && (
                        <p className={`max-w-2xl ${SECTION_SUBTITLE_CLASSES}`}>{catConfig.description}</p>
                      )}
                    </header>

                    <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
                      {/* Segment filter pills */}
                      <div className="flex flex-wrap gap-2">
                        {segmentPills.map((pill) => {
                          const href = buildCatalogUrl(activeCategory, pill.value)
                          return (
                            <a
                              key={pill.value}
                              href={href}
                              onClick={(e) => {
                                // Modified clicks keep the browser's open-in-new-tab behavior
                                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                                e.preventDefault()
                                setActiveSegment(pill.value)
                                updateUrl(activeCategory, pill.value)
                              }}
                              aria-current={activeSegment === pill.value ? 'true' : undefined}
                              className={`inline-flex h-8 items-center rounded-md border px-3 text-[13px] font-medium transition-colors motion-reduce:transition-none ${
                                activeSegment === pill.value
                                  ? 'border-primary-ink-100 bg-primary-ink-100 text-white'
                                  : 'border-primary-ink-10 bg-white text-primary-ink-60 hover:border-primary-ink-30 hover:text-primary-ink-100'
                              }`}>
                              {pill.label}
                            </a>
                          )
                        })}
                      </div>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                        className="h-9 shrink-0 appearance-none self-start rounded-md border border-primary-ink-10 bg-white bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236B757A%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_10px_center] bg-no-repeat pl-3 pr-8 text-sm text-primary-ink-100 transition-colors hover:border-primary-ink-30 focus:rounded-md focus:border-primary-green-85 focus:outline-none focus:ring-0 lg:self-auto"
                        aria-label="Sort products">
                        <option value="popularity">Sort by popularity</option>
                        <option value="price-asc">Price: low to high</option>
                        <option value="price-desc">Price: high to low</option>
                      </select>
                    </div>

                    {sorted.length === 0 && (
                      <p className="rounded-lg border border-primary-ink-10 bg-white p-6 text-sm text-primary-ink-60">
                        No{' '}
                        {activeSegment !== 'all' &&
                          `${SEGMENT_PILLS.find((pill) => pill.value === activeSegment)?.label} `}
                        products in {catConfig?.name ?? 'this category'} yet.
                      </p>
                    )}

                    {/* Product grid */}
                    <div className={GRID_CLASSES}>
                      {sorted.slice(0, expanded[activeCategory] ? sorted.length : INITIAL_SHOW).map((p) => (
                        <ProductCard
                          key={p.slug}
                          product={p}
                          activeSegment={activeSegment !== 'all' ? activeSegment : undefined}
                        />
                      ))}
                    </div>

                    {/* Load more */}
                    {!expanded[activeCategory] && sorted.length > INITIAL_SHOW && (
                      <LoadMoreButton
                        remaining={sorted.length - INITIAL_SHOW}
                        onClick={() => setExpanded((prev) => ({ ...prev, [activeCategory]: true }))}
                      />
                    )}

                    {/* Secureframe Defense cross-sell — GCC/GCC High category views only (MAR-994) */}
                    {(activeSegment === 'gcc' || activeSegment === 'gcc-high') && (
                      <div className="mt-10">
                        <DefenseCrossSell category={activeCategory} segment={activeSegment} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
